/**
 * @file tree.component.tsx
 * @description Компонент для отображения древовидного списка с возможностью выбрать элемент/элементы. Основан на DHX Tree с поддержкой чекбоксов, редактирования элементов, группировки и настраиваемых шаблонов отображения. Используется в каталогах для фильтрации по группам и навигации по иерархическим структурам.
 * @author Genotroid
 * @created 2025-09-03
 * @updated 2025-09-19
 * @dependencies ['dhx-suite', '@consta/icons/IconEdit', 'react-dom/server', 'classnames', 'react']
 * @related-files ['src/features/catalog/components/groups.component.tsx', 'src/features/catalog/components/group-slider.component.tsx']
 */

import { useRef, useEffect, useState } from 'react';
import 'dhx-suite/codebase/suite.js';
import 'dhx-suite/css';
import { IconEdit } from '@consta/icons/IconEdit';
import { renderToString } from 'react-dom/server';
import cn from 'classnames';
import type { ITreeItem, ITreeProps, IDhxTreeInstance } from '@src/types/tree-components';

export const Tree = ({
    data = [],
    onCheckItems,
    onItemClick,
    disabled = false,
    onEditButtonClick,
    checkbox = false,
    groupIcons = false,
    collapsed = true,
    className = ''
}: ITreeProps) => {
    const node = useRef<HTMLDivElement>(null);
    const [tree, setTree] = useState<IDhxTreeInstance | null>(null);

    useEffect(() => {
        if (!node.current) {
            return;
        }

        const component = new window.dhx.Tree(node.current, {
            data,
            css: cn('dhxTree overflow-x-hidden bg-transparent', { dhxTreeDisabled: disabled }, className),
            checkbox: Boolean(onCheckItems) && checkbox,
            collapsed,
            itemHeight: '2.5397vmin',
            selection: false,
            editable: false,
            isFolder: ({ isGroup }: ITreeItem) => isGroup || groupIcons,
            template: ({ name, id }: ITreeItem) => {
                let templateRow = `<div class="dhx_tree_template flex justify-between">
                        <span class="dhx_tree_template__value mr-[0.6095vmin]" title="${name}">${name}</span>`;

                if (onEditButtonClick && id && id !== import.meta.env.VITE_EMPTY_GUID) {
                    templateRow += `<div class="dhx_tree_template__rows dhxTree_toolIcon mr-[0.4064vmin] edit">
                            ${renderToString(<IconEdit size="xs" />)}
                    </div>`;
                }

                templateRow += '</div>';

                return templateRow;
            },
            eventHandlers: {
                onclick: {
                    edit: (_event: Event, { id }: { id: string }) => {
                        const item = data.find((row: ITreeItem) => row.id === id);
                        if (item && onEditButtonClick) {
                            onEditButtonClick(item);
                        }
                    }
                }
            }
        });

        onCheckItems && component.events.on('afterCheck', () => onCheckItems(component.getChecked()));

        if (onItemClick) {
            component.events.on('itemClick', (id?: string) => {
                if (id) {
                    const object = component.data.getItem(id);
                    onItemClick(object);
                }
            });
        }

        setTree(component);

        return () => component.destructor();
    }, []);

    // useEffect(() => {
    //     if (!tree) {
    //         return;
    //     }

    //     tree.config.css = cn('dhxTree overflow-x-hidden pr-[1.5878vmin]', {
    //         dhxTreeDisabled: disabled
    //     });
    //     tree.paint();
    // }, [disabled]);

    useEffect(() => {
        if (!tree) {
            return;
        }

        tree.data.parse(data);
    }, [data]);

    return <div ref={node} />;
};
